'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import { authStore, expireSessionIfTokenExpired } from '@/store/auth.store';
import { setOpenSignInModal } from '@/store/modal';
import { openPatientSelection } from '@/store/patient-selection/patient-selection.store';
import { CartOrigin } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { LoginOriginEnum } from '@/types/enums/analytics/login/login-analytics.enum';
import { PatientOption, ScheduleAgeRange } from '@/types/patient-selection.types';
import { AnalyticsScheduleType } from '@/types/enums/analytics/search/search-analytics.enum';
import { getCartOrigin } from '@/utils/cart/get-cart-origin.util';

export interface PatientSelectionParams {
  ageRange?: ScheduleAgeRange;
  scheduleType?: AnalyticsScheduleType;
  canSearchAnotherOption?: boolean;
  onBeforeSelection?: () => Promise<boolean>;
  onSelectPatient?: (patient: PatientOption) => void;
}

export interface UsePatientSelectionReturn {
  requestPatientSelection: (params: PatientSelectionParams) => void;
}

const loginOriginByCartOrigin: Partial<Record<CartOrigin, LoginOriginEnum>> = {
  [CartOrigin.SEARCH_RESULTS]: LoginOriginEnum.SEARCH_RESULT,
  [CartOrigin.PROFILE]: LoginOriginEnum.ENTITY,
};

function onceAuthResolves(run: () => void): () => void {
  if (authStore.getState().computed.isAuthReady) {
    run();
    return () => {};
  }

  const unsubscribe = authStore.subscribe((state) => {
    if (state.authStatus !== 'loading') {
      unsubscribe();
      run();
    }
  });

  return unsubscribe;
}

export function usePatientSelection(): UsePatientSelectionReturn {
  const pathname = usePathname();
  const cancelPendingRequest = useRef<() => void>(() => {});

  useEffect(() => () => cancelPendingRequest.current(), []);

  const requestPatientSelection = useCallback((
    { ageRange, scheduleType, canSearchAnotherOption, onBeforeSelection, onSelectPatient }: PatientSelectionParams,
  ) => {
    cancelPendingRequest.current();

    const openSelection = async () => {
      if (onBeforeSelection && !(await onBeforeSelection())) {
        return;
      }

      openPatientSelection({
        ageRange: ageRange ?? {},
        scheduleType,
        canSearchAnotherOption,
        onSelectPatient,
      });
    };

    cancelPendingRequest.current = onceAuthResolves(() => {
      expireSessionIfTokenExpired();

      if (!authStore.getState().computed.isLoggedIn) {
        setOpenSignInModal(true, {
          origin: loginOriginByCartOrigin[getCartOrigin(pathname)],
          loginCallback: () => void openSelection(),
        });
        return;
      }

      void openSelection();
    });
  }, [pathname]);

  return { requestPatientSelection };
}
